import { isFinite as _isFinite } from 'lodash-es';
import { Fragment } from 'react';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { makeStyles } from 'tss-react/mui';
import MaterialLinkButton from './material-link-button';
import SocialLinks from './social-links';
import Markdown from './markdown';
import ImageUpload from './image-upload';
import SectionHeader from './section-header';
import JoinButton from './groups/join-button';
import Link from 'next/link';
import type { Group } from '../types/Group';

type Props = {
  group: Group;
};

const useStyles = makeStyles()((theme) => ({
  hostContent: {
    display: 'flex',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  hostPhoto: {
    paddingRight: 15,
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },
  hostSummary: {
    flexGrow: 1,
    '& > p:first-of-type': {
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  chips: {
    margin: theme.spacing(2, 0),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
}));

const HostGroupSummary = ({ group }: Props) => {
  const { classes } = useStyles();
  return (
    <Fragment>
      <SectionHeader
        action={
          <JoinButton
            group={group}
            size="small"
          />
        }
      >
        About {group.name}
      </SectionHeader>
      <Paper sx={{ p: 3, mb: 3 }}>
        <div className={classes.hostContent}>
          {group.image && (
            <div className={classes.hostPhoto}>
              <ImageUpload record={group} size="200x150iwe" />
            </div>
          )}
          <div className={classes.hostSummary}>
            <Markdown>{group.summary}</Markdown>
            <SocialLinks source={group} />
            <div className={classes.chips}>
              {group.pastEventsCount > 0 && (
                <Chip
                  size="small"
                  label={`${group.pastEventsCount} past event${group.pastEventsCount !== 1 ? 's' : ''}`}
                  variant="outlined"
                  component={Link}
                  href={`/groups/${group.slug}/calendar/past`}
                  clickable
                />
              )}
              {_isFinite(group.membersCount) && (
                <Chip
                  size="small"
                  label={`${group.membersCount} member${group.membersCount !== 1 ? 's' : ''}`}
                  variant="outlined"
                />
              )}
            </div>
            <MaterialLinkButton
              href={`/groups/${group.slug}`}
              variant="outlined"
            >
              {group.name}
            </MaterialLinkButton>
          </div>
        </div>
      </Paper>
    </Fragment>
  );
}

export default HostGroupSummary;
